import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  NgbActiveModal,
  NgbTypeahead,
  NgbTypeaheadSelectItemEvent,
} from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { Client, Country, Organization } from '../../shared/sdk';

import { WhiteLabelService } from 'src/app/shared/services/whitelabel.service';
import { OrganizationService } from '../../organizations/organization.service';
import { validEmailValidator } from '../../shared/directives/valid-email.directive';
import { CompanyService } from '../../shared/services/company.service';
import { CountryService } from '../../shared/services/country.service';
import { ClientService } from '../client.service';

@Component({
  selector: 'app-client-add',
  templateUrl: './client-add.component.html',
  styleUrls: ['./client-add.component.css'],
})
export class ClientAddComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() client: Client;
  @Input() hideEndCustomerSwitch: boolean;
  @Input() isDocumentClient: boolean;
  @Input() validateForEslog: boolean;
  @Input() validateOnInit: boolean;
  @ViewChild('clientName') clientName: ElementRef;
  @ViewChild('clientNameExists') clientNameExists: ElementRef;
  @ViewChild('country', { static: true }) country: NgbTypeahead;
  click$ = new Subject<string>();
  clientForm: FormGroup;
  companySearchDataSource: Observable<any>;
  countries: Country[];
  focus$ = new Subject<string>();
  loading: boolean;
  loadingCompanies: boolean;
  sent: boolean;
  save = true; // Should the change be persisted, false in case only updating client on document?
  editing: boolean;
  isCompanySearchAllowed = true;
  private activeOrg: Organization;
  private onDestroy$: Subject<void> = new Subject();

  constructor(
    public wls: WhiteLabelService,
    private activeModal: NgbActiveModal,
    private clientService: ClientService,
    private companyService: CompanyService,
    private countryService: CountryService,
    private orgService: OrganizationService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.clientForm = this.fb.group({
      id: [],
      name: ['', Validators.required],
      address: [''],
      address2: [''],
      zip: [''],
      city: [''],
      country: [''],
      taxNumber: [''],
      companyNumber: [''],
      taxSubject: [true],
      email: ['', validEmailValidator()],
      contact: [''],
      phone: [''],
      SWIFT: [''],
      IBAN: [''],
      bank: [''],
      isEndCustomer: [false],
      type: [''],
      _data: this.fb.array([]),
      custom: [],
    });
    this.loadingCompanies = false;
    this.save = !this.isDocumentClient;

    this.getCountries();

    if (!this.client) {
      this.client = new Client();
    } else {
      this.client = new Client(this.client);
      if (!this.client._data) {
        this.client._data = [];
      }
      if (!this.client.type) {
        this.client.type = '';
      }
      this.clientForm.patchValue(this.client);

      const _data = this.client._data;
      if (_data && Array.isArray(_data)) {
        for (let i = 0, len = _data.length; i < len; i++) {
          const d = _data[i];
          this.addData(this.initData(d));
        }
      }
    }

    if (this.validateForEslog) {
      this.sent = !!this.validateOnInit;
      this.toggleEslogValidators(!this.clientForm.controls.isEndCustomer.value);
    }

    // TODO: Trigger typeahead if adding new (from document add component)

    this.orgService.active
      .pipe(filter((org) => !!org))
      .subscribe((org: Organization) => {
        this.activeOrg = org;

        if (this.clientForm.controls.country.value === '') {
          this.clientForm.controls.country.patchValue(this.activeOrg.country);
        }
      });

    // Auto add/remove SI to tax number
    this.clientForm.controls.taxSubject.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((isTaxSubject) => {
        const taxNumberCtrl = this.clientForm.controls.taxNumber;
        const country = this.clientForm.controls.country.value;

        if (country === 'Slovenija' && taxNumberCtrl.value) {
          if (isTaxSubject && taxNumberCtrl.value.indexOf('SI') === -1) {
            taxNumberCtrl.patchValue('SI' + taxNumberCtrl.value, {
              onlySelf: true,
              emitEvent: false,
            });
          } else if (
            !isTaxSubject &&
            taxNumberCtrl.value.indexOf('SI') !== -1
          ) {
            taxNumberCtrl.patchValue(taxNumberCtrl.value.slice(2), {
              onlySelf: true,
              emitEvent: false,
            });
          }
        }
      });

    // Max 2 custom data on PDF
    // Force checked when editing document fields
    this.dataFA.valueChanges
      .pipe(debounceTime(200), takeUntil(this.onDestroy$))
      .subscribe((val) => {
        let c = 0;

        for (let i = 0, len = val.length; i < len; i++) {
          const d = val[i];

          if (c >= 2 || !this.save) {
            this.clientForm
              .get(`_data.${i}.options.onPDF`)
              .patchValue(!this.save, {
                emitEvent: false,
                onlySelf: true,
              });
          } else {
            if (d.options && d.options.onPDF) {
              c++;
            }
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  ngAfterViewInit(): void {
    const focusEl = this.clientName ? 'clientName' : 'clientNameExists';
    setTimeout(() => this[focusEl].nativeElement.focus(), 100);
  }

  companySearch = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(500),
      tap(() => (this.loadingCompanies = true)),
      switchMap((term: string) => {
        if (
          !this.isCompanySearchAllowed ||
          this.activeOrg.countryAlpha2Code !== 'SI'
        ) {
          return of([]);
        }

        return this.findCompanies(
          term,
          this.clientForm.get('country').value,
        ).pipe(catchError(() => of([])));
      }),
      tap(() => (this.loadingCompanies = false)),
      takeUntil(this.onDestroy$),
    );

  get dataFA(): FormArray {
    return this.clientForm.controls._data as FormArray;
  }

  onAddData(): void {
    this.addData(this.initData());
  }

  onClose(): void {
    this.activeModal.dismiss();
  }

  onRemoveData(i: number): void {
    this.removeData(i);
  }

  onSave(): void {
    this.sent = true;

    if (this.clientForm.valid) {
      this.client = this.clientForm.value;

      if (this.save) {
        this.editing = !!this.client.id;
        this.clientService[!this.editing ? 'create' : 'update'](
          !this.editing ? this.activeOrg.id : this.client.id,
          this.client,
        ).subscribe((client: Client) => {
          this.activeModal.close(client);
        });
      } else {
        this.activeModal.close(this.client);
      }
    }
  }

  onSelectCompany(match: NgbTypeaheadSelectItemEvent): void {
    if (match && match.item) {
      const client = match.item as Client;
      delete client.id;

      if (
        client.country === 'Slovenija' &&
        client.taxNumber &&
        client.taxNumber.indexOf('SI') === -1
      ) {
        client.taxNumber = 'SI' + client.taxNumber;
        client.country = 'SLOVENIJA';
      }

      this.clientForm.patchValue(client);

      match.preventDefault();
    }
  }

  onToggleClientType(isCompany: boolean): void {
    const controls = ['bank', 'IBAN', 'SWIFT', 'taxSubject'];
    let enable = false;
    this.toggleEslogValidators(isCompany);

    if (!isCompany) {
      this.isCompanySearchAllowed = false;

      this.clientForm.patchValue({
        isEndCustomer: !this.clientForm.get('isEndCustomer').value,
        bank: '',
        IBAN: '',
        SWIFT: '',
        taxSubject: false,
        taxNumber: '',
        type: 'customer',
      });
    } else {
      enable = true;
      this.isCompanySearchAllowed = true;

      this.clientForm.patchValue({
        isEndCustomer: !this.clientForm.get('isEndCustomer').value,
        taxSubject: true,
        type: '',
      });
    }

    for (let i = 0, len = controls.length; i < 0; i++) {
      const ctrl = this.clientForm.controls[i];
      ctrl[enable ? 'enable' : 'disable']();
    }
  }

  toggleEslogValidators(isCompany: boolean): void {
    const controls = [
      'address',
      'zip',
      'city',
      'country',
      'taxNumber',
      'companyNumber',
    ];
    const validate = this.validateForEslog && isCompany;

    for (let i = 0, len = controls.length; i < len; i++) {
      const ctrl = controls[i];

      if (validate) {
        this.clientForm.controls[ctrl].setValidators(Validators.required);
      } else {
        this.clientForm.controls[ctrl].clearValidators();
      }

      this.clientForm.controls[ctrl].updateValueAndValidity();
    }
  }

  private addData(dataCtrl: FormGroup): void {
    this.dataFA.push(dataCtrl);
  }

  private findCompanies(term: string, country: string): Observable<any> {
    if (term === '') return of([]);
    return this.companyService.find(term, country);
    // .pipe(map({ body }) => body);
  }

  private getCountries(): void {
    this.countryService
      .get()
      .pipe(map(({ body }) => body))
      .subscribe((countries) => {
        this.countries = countries;
      });
  }

  private initData(data: any = {}): FormGroup {
    const fg = this.fb.group({
      name: ['', Validators.required],
      value: [''],
      options: this.fb.group({
        onPDF: [!this.save],
      }),
    });

    fg.patchValue(data, { emitEvent: false, onlySelf: true });
    return fg;
  }

  private removeData(i: number): void {
    this.dataFA.removeAt(i);
  }
}
